import { useTheme } from '../../../hooks/useTheme';
import { Card, Button, Pill } from '@dsi/react-eds';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { MoshellEndpoint, EnvironmentCategory } from './moshell-endpoint-interfaces';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { useLoader } from '../../../hooks/useLoader';
import api from '../../../api/axiosInstance';
import styles from './MoshellForm.module.css';
import { AxiosError } from 'axios';
import ConfirmDialog from '../../common/ConfirmDialog/ConfirmDialog';
import LinkEnmsDialog from './LinkEnmsDialog';
import { moshellService } from '../../../services/moshellService';

const Moshell = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();

  const [moshellEndpoints, setMoshellEndpoints] = useState<MoshellEndpoint[]>([]);
  const [filteredMoshellEndpoints, setFilteredMoshellEndpoints] = useState<MoshellEndpoint[]>([]);
  const [selectedMoshellEndpoint, setSelectedMoshellEndpoint] = useState<MoshellEndpoint>();
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const categoryPills: EnvironmentCategory[] = ['Production', 'Staging', 'Development', 'Testing'];
  const [filteredCategories, setFilteredCategories] = useState<EnvironmentCategory[]>(categoryPills);

  // Link ENMs Dialog state
  const [linkEnmsDialogOpen, setLinkEnmsDialogOpen] = useState(false);
  const [selectedMoshell, setSelectedMoshell] = useState<MoshellEndpoint>();

  // Fetch EIAP clusters from backend on component mount
  useEffect(() => {
    fetchMoshellEndpoints();
  }, []);

  useEffect(() => {
    if (filteredCategories.length > 0) {
      const filtered = moshellEndpoints.filter((ce) => filteredCategories.includes(ce.category));
      setFilteredMoshellEndpoints(filtered);
    } else {
      setFilteredMoshellEndpoints(moshellEndpoints);
    }
  }, [filteredCategories]);

  const fetchMoshellEndpoints = async () => {
    try {
      showLoader();
      const response = await moshellService.getAll();
      setMoshellEndpoints(response.data);
      setFilteredMoshellEndpoints(response.data);
    } catch (error) {
      addNotifications([
        {
          title: 'Error - Moshell Endpoints',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching Moshell Endpoints.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  };

  const handleCreateMoshellEndpoint = () => {
    navigate('/integrations/moshell/new');
  };

  const handleEditMoshellEndpoint = (id: string) => {
    navigate(`/integrations/moshell/edit/${id}`);
  };

  const handleDeleteMoshellEndpoint = (moshellEndpoint: MoshellEndpoint) => {
    setSelectedMoshellEndpoint(moshellEndpoint);
    setOpenDialog(true);
  };

  const handleCancelDeleteMoshellEndpoint = () => {
    setSelectedMoshellEndpoint(undefined);
    setOpenDialog(false);
  };

  const deleteMoshellEndpoint = async () => {
    if (selectedMoshellEndpoint) {
      try {
        showLoader();
        await api.delete(`/api/moshell-endpoint/${selectedMoshellEndpoint.id}`);
        addNotifications([
          {
            title: `${selectedMoshellEndpoint.name} removed succesfully`,
            icon: 'icon-success',
            description: `The Moshell Endpoint has been succesfully removed.`,
            timestamp: new Date(),
            color: 'green'
          }
        ]);
        // Refresh the list after deletion
        await fetchMoshellEndpoints();
        setOpenDialog(false);
      } catch (error) {
        addNotifications([
          {
            title: `Failed to remove ${selectedMoshellEndpoint.name}`,
            icon: 'icon-triangle-warning',
            description:
              error && error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : 'The system was unable to remove the Moshell Endpoint. Plase try again.',
            timestamp: new Date(),
            color: 'red'
          }
        ]);
      } finally {
        setTimeout(() => {
          hideLoader();
        }, 500);
      }
    }
  };

  const handleLinkEnms = (moshell: MoshellEndpoint) => {
    setSelectedMoshell(moshell);
    setLinkEnmsDialogOpen(true);
  };

  const handleSaveLinkEnms = async (endpointId: string, linkedEnmIds: string[]) => {
    try {
      showLoader();
      await api.put(`/api/moshell-endpoint/${endpointId}/link-enms`, { enmIds: linkedEnmIds });
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: `ENM links updated successfully!`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
      await fetchMoshellEndpoints();
      setLinkEnmsDialogOpen(false);
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError ? error.response?.data?.message || error.message : 'An error occurred while linking ENMs.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  };

  const handleCloseLinkEnmsDialog = () => {
    setLinkEnmsDialogOpen(false);
    setSelectedMoshell(undefined);
  };

  const getCategoryTagClass = (category: MoshellEndpoint['category']) => {
    switch (category) {
      case 'Production':
        return 'red';
      case 'Staging':
        return 'yellow';
      case 'Development':
        return 'blue';
      case 'Testing':
        return 'green';
      default:
        return 'purple';
    }
  };

  return (
    <>
      <div className={`${theme ? 'light-theme' : 'dark-theme'}`}>
        {/* Header with Create Button */}

        <div className={`${styles.container} ${styles.containerPadding} ${theme ? styles.containerLight : styles.containerDark}`}>
          <div className="header">
            <span className={styles.title}>Moshell</span>
          </div>
          <p className={styles.subtitle}>Set up a Moshell Endpoint by defining its connection details and SFTP settings.</p>
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start' }}>
            <ReactEDSWrapper component={Button} primary onClick={handleCreateMoshellEndpoint}>
              <i className="icon icon-plus" /> Create New Moshell Endpoint
            </ReactEDSWrapper>
          </div>

          {/* Category Filter Pills */}
          <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {categoryPills.map((category) => (
              <ReactEDSWrapper
                key={category}
                component={Pill}
                toggle
                unselected={!filteredCategories.includes(category)}
                onToggle={(unselected) => {
                  if (unselected) {
                    setFilteredCategories([...filteredCategories, category]);
                  } else if (filteredCategories.length === categoryPills.length) {
                    setFilteredCategories([category]);
                  } else if (filteredCategories.length === 1) {
                    setFilteredCategories(categoryPills);
                  } else {
                    setFilteredCategories(filteredCategories.filter((cat) => cat !== category));
                  }
                }}
              >
                {category}
              </ReactEDSWrapper>
            ))}
            <div style={{ color: '#6a6a6a' }}>
              {filteredMoshellEndpoints.length} Moshell Endpoint{filteredMoshellEndpoints.length !== 1 ? 's' : ''}
            </div>
            {filteredCategories.length < categoryPills.length && (
              <a
                href="javascript:void(0);"
                onClick={() => setFilteredCategories(categoryPills)}
                style={{ marginLeft: '8px', fontSize: '14px' }}
              >
                Select all
              </a>
            )}
          </div>

          <div className="row">
            <div className="column sm-12">
              <div className="row">
                {filteredMoshellEndpoints.length === 0 && (
                  <div className={`column sm-12 ${styles.noDataContainer}`}>
                    <i className={`icon icon-info ${styles.noDataIcon}`} />
                    <div className={styles.noDataTitle}>There is no Moshell Endpoints created</div>
                    <p className={styles.subtitle}>Please add a new Moshell Endpoint.</p>
                  </div>
                )}
                {filteredMoshellEndpoints.map((MoshellEndpoint) => (
                  <div key={MoshellEndpoint.id} className={`column sm-12 md-6 xl-4 ${styles.noBorder}`}>
                    <ReactEDSWrapper component={Card} className={styles.cardNoMargin} title={MoshellEndpoint.name}>
                      {/* Description */}
                      <p style={{ marginBottom: '12px', color: '#6a6a6a' }}>{MoshellEndpoint.description}</p>

                      {/* Category Badge */}
                      <div style={{ marginBottom: '12px' }}>
                        <kbd className={`tag big ${getCategoryTagClass(MoshellEndpoint.category)}`}>{MoshellEndpoint.category}</kbd>
                      </div>

                      {/* Endpoint Badges */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', marginBottom: '12px' }}>
                        <ReactEDSWrapper key={`${MoshellEndpoint.id}`} component={Pill}>
                          <i className="icon icon-link" /> {MoshellEndpoint.endpoint}
                        </ReactEDSWrapper>
                      </div>

                      {/* Linked ENMs Info */}
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                        <i className="icon icon-network" style={{ marginRight: '8px', color: '#6a6a6a' }} />
                        <span style={{ color: '#6a6a6a' }}>
                          {`${MoshellEndpoint.moshellEnms?.length || 0} ENM${(MoshellEndpoint.moshellEnms?.length || 0) > 1 ? 's' : ''} linked`}
                        </span>
                      </div>

                      {/* Action Buttons */}
                      <div>
                        <ReactEDSWrapper
                          component={Button}
                          variant="secondary"
                          onClick={() => MoshellEndpoint.id && handleLinkEnms(MoshellEndpoint)}
                        >
                          Link ENMs
                        </ReactEDSWrapper>
                        <ReactEDSWrapper
                          component={Button}
                          variant="secondary"
                          onClick={() => MoshellEndpoint.id && handleEditMoshellEndpoint(MoshellEndpoint.id)}
                        >
                          <i className="icon icon-edit" /> Edit
                        </ReactEDSWrapper>
                        <ReactEDSWrapper
                          component={Button}
                          variant="secondary"
                          onClick={() => MoshellEndpoint.id && handleDeleteMoshellEndpoint(MoshellEndpoint)}
                        >
                          <i className="icon icon-trashcan" /> Delete
                        </ReactEDSWrapper>
                      </div>
                    </ReactEDSWrapper>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog
        onConfirm={deleteMoshellEndpoint}
        onCancel={handleCancelDeleteMoshellEndpoint}
        classes="smallDialog"
        open={openDialog}
        title={'Remove Moshell Endpoint'}
        subtitle={'Moshell'}
        buttonText={'Remove Moshell Endpoint'}
        innerText={`You're about to remove this Moshell Endpoint from the list. 
                This action is permanent and cannot be undone 
                <br /><br />
                Are you sure you want to continue?`}
      />

      {/* Link ENMs Dialog */}
      <LinkEnmsDialog open={linkEnmsDialogOpen} moshell={selectedMoshell} onSave={handleSaveLinkEnms} onClose={handleCloseLinkEnmsDialog} />
    </>
  );
};
export default Moshell;
